import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import RefreshIcon from "@material-ui/icons/Refresh";
import SendIcon from "@material-ui/icons/Send";
import LinkIcon from "@material-ui/icons/Link";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import AttachFileIcon from "@material-ui/icons/AttachFile";

import api from "../../services/api";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import {
  useViewMode,
  ViewModeToggle,
  RecordsCardsGrid,
  RecordCard,
  RecordCardMeta,
} from "../../components/ViewModeToggle";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#e0e0e0"}`
  },
  toolbar: {
    display: "grid",
    gridTemplateColumns: "minmax(240px, 360px) auto",
    gap: theme.spacing(1.5),
    alignItems: "center",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  resolveBar: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  sendPanel: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 320px) 1fr auto auto",
    gap: theme.spacing(1),
    alignItems: "center",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  codeBox: {
    fontFamily: "monospace",
    fontSize: 12,
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.mode === "dark" ? "#111827" : "#f5f5f5",
    color: theme.palette.text.primary,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  tableHeader: {
    fontWeight: 700,
    backgroundColor: theme.palette.primary.main,
    color: "#fff"
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary
  },
  jid: {
    fontFamily: "monospace",
    fontSize: 12
  },
  hiddenInput: {
    display: "none"
  }
}));

const normalizeApiBase = () => {
  const base = api.defaults.baseURL || "";
  return base.replace(/\/$/, "");
};

const NewslettersWA = () => {
  const classes = useStyles();
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useViewMode("newslettersWA");
  const [resolveValue, setResolveValue] = useState("");
  const [sendingJid, setSendingJid] = useState("");
  const [sendBody, setSendBody] = useState("");
  const [sendMedia, setSendMedia] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const loadWhatsapps = async () => {
      try {
        const { data } = await api.get("/whatsapp/?session=0");
        const list = Array.isArray(data) ? data : [];
        setWhatsapps(list);
        const connected = list.find(item => item.status === "CONNECTED");
        if (connected) setSelectedWhatsapp(String(connected.id));
      } catch (error) {
        toast.error("No se pudieron cargar las conexiones WhatsApp");
      }
    };
    loadWhatsapps();
  }, []);

  const selectedWhatsappData = useMemo(
    () => whatsapps.find(item => String(item.id) === String(selectedWhatsapp)),
    [whatsapps, selectedWhatsapp]
  );

  const apiExample = useMemo(() => {
    const whatsappId = selectedWhatsapp || ":whatsappId";
    const jid = sendingJid || "123456789@newsletter";
    return `curl -X POST "${normalizeApiBase()}/api/messages/send-newsletter/${whatsappId}" \\
  -H "Authorization: Bearer TOKEN_DE_LA_CONEXION" \\
  -F "newsletterJid=${jid}" \\
  -F "body=Texto que acompaña la multimedia" \\
  -F "medias=@/ruta/imagen.jpg"`;
  }, [selectedWhatsapp, sendingJid]);

  const loadNewsletters = async (options = {}) => {
    const silent = Boolean(options.silent);
    if (!selectedWhatsapp) {
      if (!silent) toast.warn("Selecciona una conexión");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/newsletters/${selectedWhatsapp}`);
      const items = Array.isArray(data.newsletters) ? data.newsletters : [];
      setNewsletters(items);
      setSendingJid(prev => (items.some(item => item.jid === prev) ? prev : (items[0]?.jid || "")));
      if (!silent) {
        toast.success(
          items.length
            ? `${items.length} canal(es) encontrados para esta conexión`
            : "No se encontraron canales en esta conexión"
        );
      }
    } catch (error) {
      setNewsletters([]);
      setSendingJid("");
      toast.error(error?.response?.data?.message || "No se pudieron listar los canales de esta conexión");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setNewsletters([]);
    setSendingJid("");
    if (!selectedWhatsapp) return undefined;
    loadNewsletters({ silent: true });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWhatsapp]);

  const resolveNewsletter = async () => {
    if (!selectedWhatsapp || !resolveValue.trim()) {
      toast.warn("Selecciona una conexión e ingresa un link o JID");
      return;
    }
    const isTelegram = String(selectedWhatsappData?.type || "").toLowerCase() === "telegram";
    if (isTelegram) {
      const jid = resolveValue.trim().startsWith("tg:")
        ? resolveValue.trim()
        : `tg:${resolveValue.trim()}`;
      setSendingJid(jid);
      setNewsletters(prev => {
        if (prev.some(item => item.jid === jid)) return prev;
        return [{ jid, name: jid, isAdmin: true, source: "manual" }, ...prev];
      });
      toast.success("Canal de Telegram listo para publicar");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/newsletters/${selectedWhatsapp}/resolve`, {
        key: resolveValue.trim()
      });
      const newsletter = data.newsletter;
      setNewsletters(prev => {
        const withoutDuplicate = prev.filter(item => item.jid !== newsletter.jid);
        return [newsletter, ...withoutDuplicate];
      });
      setSendingJid(newsletter.jid);
      toast.success(newsletter.isAdmin ? "Canal administrado detectado" : "Canal detectado, pero esta conexión no es admin");
    } catch (error) {
      toast.error(error?.response?.data?.message || "No se pudo resolver el canal");
    } finally {
      setLoading(false);
    }
  };

  const sendTest = async () => {
    if (!selectedWhatsapp || !sendingJid || (!sendBody.trim() && !sendMedia)) {
      toast.warn("Selecciona canal y agrega texto o multimedia");
      return;
    }
    setSending(true);
    try {
      const formData = new FormData();
      formData.append("newsletterJid", sendingJid);
      formData.append("body", sendBody);
      if (sendMedia) formData.append("medias", sendMedia);

      const { data } = await api.post(`/newsletters/${selectedWhatsapp}/send`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (!data?.success) {
        toast.warn(data?.message || "El intento se transmitió, pero no se verificó la publicación");
        return;
      }

      setSendBody("");
      setSendMedia(null);
      toast.success("Contenido publicado y verificado en el canal");
    } catch (error) {
      toast.error(error?.response?.data?.message || "No se pudo enviar al canal");
    } finally {
      setSending(false);
    }
  };

  const copyApiExample = async () => {
    try {
      await navigator.clipboard.writeText(apiExample);
      toast.success("Ejemplo copiado");
    } catch {
      toast.warn("No se pudo copiar automáticamente");
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Canales</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={loadNewsletters}
            disabled={loading || !selectedWhatsapp}
          >
            Escanear
          </Button>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        <div className={classes.toolbar}>
          <FormControl variant="outlined">
            <InputLabel>Conexión activa</InputLabel>
            <Select
              value={selectedWhatsapp}
              onChange={event => {
                setSelectedWhatsapp(event.target.value);
                setNewsletters([]);
                setSendingJid("");
              }}
              label="Conexión activa"
            >
              {whatsapps.map(whatsapp => (
                <MenuItem key={whatsapp.id} value={String(whatsapp.id)}>
                  {whatsapp.name || `Conexión ${whatsapp.id}`}
                  {String(whatsapp.type || "").toLowerCase() === "telegram" ? " · Telegram" : " · WhatsApp"}
                  {whatsapp.status ? ` (${whatsapp.status})` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="textSecondary">
            {String(selectedWhatsappData?.type || "").toLowerCase() === "telegram"
              ? "Telegram: se listan canales donde esta cuenta puede publicar. También puedes usar Campañas de grupos/canales."
              : selectedWhatsappData?.token
                ? "Usa el token de esta conexión para publicar por API."
                : "El token se configura editando la conexión."}
          </Typography>
        </div>

        <div className={classes.resolveBar}>
          <TextField
            variant="outlined"
            label={
              String(selectedWhatsappData?.type || "").toLowerCase() === "telegram"
                ? "ID del canal (tg:…)"
                : "Link del canal o JID @newsletter"
            }
            value={resolveValue}
            onChange={event => setResolveValue(event.target.value)}
            placeholder={
              String(selectedWhatsappData?.type || "").toLowerCase() === "telegram"
                ? "tg:-1001234567890"
                : "https://whatsapp.com/channel/... o 123456@newsletter"
            }
          />
          <Button
            variant="outlined"
            color="primary"
            startIcon={<LinkIcon />}
            onClick={resolveNewsletter}
            disabled={loading || !selectedWhatsapp}
          >
            Validar
          </Button>
        </div>

        <div className={classes.sendPanel}>
          <TextField
            variant="outlined"
            label={
              String(selectedWhatsappData?.type || "").toLowerCase() === "telegram"
                ? "ID del canal Telegram"
                : "JID del canal"
            }
            value={sendingJid}
            onChange={event => setSendingJid(event.target.value)}
          />
          <TextField
            variant="outlined"
            label="Contenido"
            value={sendBody}
            onChange={event => setSendBody(event.target.value)}
          />
          <input
            accept="image/*,video/*,audio/*,application/pdf"
            className={classes.hiddenInput}
            id="newsletter-media-input"
            type="file"
            onChange={event => setSendMedia(event.target.files?.[0] || null)}
          />
          <label htmlFor="newsletter-media-input">
            <Button
              component="span"
              variant={sendMedia ? "contained" : "outlined"}
              color={sendMedia ? "primary" : "default"}
              startIcon={<AttachFileIcon />}
            >
              {sendMedia ? "Adjunto" : "Adjuntar"}
            </Button>
          </label>
          <Button
            variant="contained"
            color="primary"
            startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
            onClick={sendTest}
            disabled={sending || !selectedWhatsapp}
          >
            Enviar
          </Button>
        </div>

        <Typography variant="subtitle2">API por token</Typography>
        <div className={classes.codeBox}>{apiExample}</div>
        <Button size="small" variant="outlined" startIcon={<FileCopyIcon />} onClick={copyApiExample}>
          Copiar ejemplo
        </Button>

        {viewMode === "cards" ? (
          <RecordsCardsGrid>
            {newsletters.map((item) => (
              <RecordCard
                key={`nl-card-${item.jid}`}
                title={item.name}
                actions={
                  <IconButton onClick={() => setSendingJid(item.jid)}>
                    <SendIcon />
                  </IconButton>
                }
              >
                <RecordCardMeta>{item.jid}</RecordCardMeta>
                <RecordCardMeta>
                  {item.isAdmin ? (item.role || "ADMIN") : (item.role || "Seguidor")}
                  {item.subscribers ? ` · ${item.subscribers}` : ""}
                </RecordCardMeta>
              </RecordCard>
            ))}
          </RecordsCardsGrid>
        ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader}>Canal</TableCell>
              <TableCell className={classes.tableHeader}>JID</TableCell>
              <TableCell className={classes.tableHeader}>Rol</TableCell>
              <TableCell className={classes.tableHeader}>Seguidores</TableCell>
              <TableCell className={classes.tableHeader}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className={classes.empty}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : newsletters.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className={classes.empty}>
                  <AnnouncementIcon />
                  <Typography>
                    {selectedWhatsappData?.status && selectedWhatsappData.status !== "CONNECTED"
                      ? `La conexión ${selectedWhatsappData.name || ""} está ${selectedWhatsappData.status}. Conéctala para listar sus canales.`
                      : "No hay canales detectados para esta conexión. Pulsa Escanear o valida un link/JID."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              newsletters.map(item => (
                <TableRow key={item.jid}>
                  <TableCell>
                    <Typography variant="body2">{item.name}</Typography>
                    {item.description && (
                      <Typography variant="caption" color="textSecondary">
                        {item.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell className={classes.jid}>{item.jid}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={item.isAdmin ? "primary" : "default"}
                      label={item.isAdmin ? (item.role || "ADMIN") : (item.role || "Seguidor")}
                    />
                  </TableCell>
                  <TableCell>{item.subscribers || "-"}</TableCell>
                  <TableCell>
                    <Tooltip title="Usar para enviar">
                      <IconButton onClick={() => setSendingJid(item.jid)}>
                        <SendIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default NewslettersWA;
